import React from "react";
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export type SlideOverPanelProps = Readonly<{
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  width?: number | string;
  maxWidth?: number | string;
  containerZIndex?: number;
}>;
/**
 * Reusable slide-over panel that opens from the right.
 * Use for add/edit forms and any secondary content that should appear as an overlay.
 */
export default function SlideOverPanel({
  open,
  onClose,
  title,
  children,
  width = 1000,
  maxWidth,
  containerZIndex,
}: SlideOverPanelProps) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const panelWidth = isSmall ? "100%" : width;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={containerZIndex != null ? { zIndex: containerZIndex } : undefined}
      BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.4)" } }}
      PaperProps={{
        sx: {
          width: panelWidth,
          maxWidth: maxWidth ?? panelWidth,
          height: "85vh",
          maxHeight: "85vh",
          top: "5%",
          transform: "translateY(-50%)",
          bottom: "auto",
          boxSizing: "border-box",
          borderTopLeftRadius: 12,
          borderBottomLeftRadius: 12,
          boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "#fff",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
          backgroundColor: "#fff",
        }}
      >
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton
          aria-label="Close panel"
          onClick={onClose}
          size="small"
          sx={{
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          p: 3,
          backgroundColor: "#fff",
          display: "block",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-track": { background: "rgba(0,0,0,0.04)" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.2)",
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(0,0,0,0.35)",
          },
        }}
      >
        {children}
      </Box>
    </Drawer>
  );
}
